import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPatientInfoLogic,
  clearPatientInfo,
} from "../../../../store/features/analytics/analyticsSlice";
import { COLORS } from "../mockData";

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const VIEW_TYPE_TABS = {
  highRiskPatients: ["included"],
  improvementTracking: ["included", "excluded"],
  totalPatientsByCondition: ["included", "excluded", "all"],
  remissionTracking: ["included", "excluded", "all"],
  partialResponse: ["included", "excluded", "all"],
  worseningSymptoms: ["included", "excluded", "all"],
  medianTimeToRemission: ["included", "excluded", "all"],
  mcidImprovement: ["included", "excluded", "all"],
  averageScoreTrend: ["included"],
  severityDistribution: ["included"],
};

const TAB_LABELS = {
  included: "Included",
  excluded: "Excluded",
  all: "All",
};

const TIME_RANGE_MAP = {
  "30": "Last 30 days",
  "60": "Last 60 days",
  "90": "Last 90 days",
  last_30_days: "Last 30 days",
  last_60_days: "Last 60 days",
  last_90_days: "Last 90 days",
  "Last 30 days": "Last 30 days",
  "Last 60 days": "Last 60 days",
  "Last 90 days": "Last 90 days",
};

const normalizeTimeRange = (tr) => TIME_RANGE_MAP[tr] ?? "Last 90 days";

const buildPatientInfoPayload = (cardConfig, viewType, appliedFilters, page, pageSize) => {
  const payload = {
    cardType: cardConfig.cardType,
    assessmentType: cardConfig.assessmentType,
    page,
    pageSize,
  };

  const effectiveViewType = viewType || cardConfig.viewType;
  if (cardConfig.cardType !== "highRiskPatients" && effectiveViewType) {
    payload.viewType = effectiveViewType;
  }

  payload.timeRange = normalizeTimeRange(cardConfig.timeRange);

  if (appliedFilters.startDate && appliedFilters.endDate) {
    payload.startDate = appliedFilters.startDate.split("T")[0];
    payload.endDate = appliedFilters.endDate.split("T")[0];
  }

  if (cardConfig.bucketIndex !== undefined) {
    payload.bucketIndex = cardConfig.bucketIndex;
  }
  if (cardConfig.severity) {
    payload.severity = cardConfig.severity;
  }

  if (appliedFilters.clinicIds?.length) {
    payload.clinicId = appliedFilters.clinicIds.map((id) => Number(id));
  }
  if (appliedFilters.patientIds?.length) {
    payload.patientId = appliedFilters.patientIds.map((p) => ({
      type: p.type,
      patientId: p.patient_id ?? null,
      healthiePatientId: p.healthie_patient_id ?? null,
    }));
  }
  if (appliedFilters.genders?.length) {
    payload.gender = appliedFilters.genders;
  }
  if (appliedFilters.ageRange) {
    payload.ageRange = appliedFilters.ageRange;
  }
  if (appliedFilters.ticDuration && !appliedFilters.ticStartDate) {
    payload.timeInCare = appliedFilters.ticDuration;
  }
  if (appliedFilters.ticStartDate) {
    payload.timeInCareStartDate = appliedFilters.ticStartDate.split("T")[0];
  }
  if (appliedFilters.ticEndDate) {
    payload.timeInCareEndDate = appliedFilters.ticEndDate.split("T")[0];
  }

  return payload;
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages, start + maxVisible - 1);
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
};

const PatientInfoModal = ({ isOpen, onClose, cardConfig, appliedFilters = {} }) => {
  const dispatch = useDispatch();
  const { patientInfoData, patientInfoLoading } = useSelector((s) => s.analytics);

  const tabs = cardConfig ? (VIEW_TYPE_TABS[cardConfig.cardType] || []) : [];
  const [activeViewType, setActiveViewType] = useState(tabs[0] || null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = useCallback(
    (viewType, pageNum, size) => {
      if (!cardConfig) return;
      const payload = buildPatientInfoPayload(cardConfig, viewType, appliedFilters, pageNum, size);
      dispatch(fetchPatientInfoLogic(payload));
    },
    [cardConfig, appliedFilters, dispatch]
  );

  // Merged effect: resets state and fetches with the correct initialViewType when
  // the modal opens or a different card is selected. Avoids the race condition where
  // setActiveViewType (async state update) would be stale when fetchData fires.
  useEffect(() => {
    if (isOpen && cardConfig) {
      const newTabs = VIEW_TYPE_TABS[cardConfig.cardType] || [];
      const initialViewType = newTabs[0] || null;
      setActiveViewType(initialViewType);
      setPage(1);
      dispatch(clearPatientInfo());
      fetchData(initialViewType, 1, pageSize);
    } else if (!isOpen) {
      // Reset local state when modal closes so next open always starts fresh
      setActiveViewType(null);
      setPage(1);
    }
  }, [isOpen, cardConfig]); // intentional: appliedFilters/pageSize captured at open time

  const handleTabClick = (viewType) => {
    if (viewType === activeViewType) return;
    setActiveViewType(viewType);
    setPage(1);
    dispatch(clearPatientInfo());
    fetchData(viewType, 1, pageSize);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    dispatch(clearPatientInfo());
    fetchData(activeViewType, newPage, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setPage(1);
    dispatch(clearPatientInfo());
    fetchData(activeViewType, 1, newSize);
  };

  const handleClose = () => {
    dispatch(clearPatientInfo());
    onClose();
  };

  if (!isOpen || !cardConfig) return null;

  const assessmentColor =
    cardConfig.assessmentType === "phq9" ? COLORS.phq9 : COLORS.gad7;
  const pagination = patientInfoData?.pagination;
  const columns = patientInfoData?.columns || [];
  const rows = patientInfoData?.rows || [];

  return (
    <div className="patient-info-modal-overlay" onClick={handleClose}>
      <div className="patient-info-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="patient-info-modal-header">
          <div className="patient-info-modal-header-left">
            <span
              className="patient-info-modal-badge"
              style={{
                background: assessmentColor + "1a",
                color: assessmentColor,
                border: `1px solid ${assessmentColor}40`,
              }}
            >
              {cardConfig.cardLabel}
            </span>
            <h2 className="patient-info-modal-title">{cardConfig.sectionTitle}</h2>
          </div>
          <button className="patient-info-modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        {/* ViewType tabs */}
        {tabs.length > 0 && (
          <div className="patient-info-modal-tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                className={`patient-info-modal-tab${activeViewType === tab ? " patient-info-modal-tab--active" : ""}`}
                style={
                  activeViewType === tab
                    ? { color: assessmentColor, borderBottomColor: assessmentColor }
                    : {}
                }
                onClick={() => handleTabClick(tab)}
                disabled={patientInfoLoading}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="patient-info-modal-body">
          {patientInfoLoading ? (
            <div className="patient-info-modal-loader">
              <div
                className="patient-info-modal-spinner"
                style={{ borderTopColor: assessmentColor }}
              />
              <span>Loading patients...</span>
            </div>
          ) : rows.length > 0 ? (
            <div className="patient-info-table-wrapper">
              <table className="patient-level-table patient-info-table">
                <thead>
                  <tr>
                    {columns.map((col) => (
                      <th key={col.key}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id}>
                      {columns.map((col) => (
                        <td key={col.key}>
                          {col.key === "score" ? (
                            <span style={{ color: assessmentColor, fontWeight: 700 }}>
                              {row[col.key] ?? "—"}
                            </span>
                          ) : (
                            row[col.key] ?? "—"
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : patientInfoData ? (
            <div className="patient-info-modal-placeholder">No patients found.</div>
          ) : (
            <div className="patient-info-modal-placeholder">No data</div>
          )}
        </div>

        {/* Pagination */}
        {pagination && !patientInfoLoading && pagination.totalCount > 0 && (
          <div className="patient-level-pagination-wrapper patient-info-pagination-wrapper">
            <div className="patient-level-pagination-row" style={{ position: "relative" }}>
              <div className="patient-level-pages">
                <button
                  className="patient-level-page-arrow"
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                >
                  <ChevronLeft />
                </button>
                {getPageNumbers(page, pagination.totalPages).map((p) => (
                  <button
                    key={p}
                    className={`patient-level-page-btn${p === page ? " patient-level-page-btn--active" : ""}`}
                    onClick={() => handlePageChange(p)}
                  >
                    {p}
                  </button>
                ))}
                <button
                  className="patient-level-page-arrow"
                  disabled={page >= pagination.totalPages}
                  onClick={() => handlePageChange(page + 1)}
                >
                  <ChevronRight />
                </button>
              </div>
              <div className="patient-level-page-size">
                <span>Rows per page</span>
                <select value={pageSize} onChange={handlePageSizeChange}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
            <div className="patient-level-pagination-info">
              Showing {pagination.from} to {pagination.to} of {pagination.totalCount} Patients
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PatientInfoModal;
